import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";

export function ReviewStep({ validation, summary }) {
  const { t } = useLanguage();
  const r = t.review;
  const isValid = validation?.is_valid;

  return (
    <div className="space-y-6" data-testid="step-review-content">
      <div>
        <h2 className="text-3xl font-black tracking-tight md:text-4xl">
          {r.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{r.description}</p>
      </div>

      {/* Overall status */}
      <div
        data-testid="review-validation-status"
        className={`hairline flex items-start gap-3 p-4 ${
          isValid
            ? "bg-[hsl(var(--primary))]/[0.04]"
            : "bg-destructive/[0.05]"
        }`}
      >
        {isValid ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5 text-[hsl(var(--primary))]" strokeWidth={2.2} />
        ) : (
          <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" strokeWidth={2.2} />
        )}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {isValid ? "READY" : "INCOMPLETE"}
          </div>
          <p className="mt-1 text-sm">
            {isValid
              ? r.valid
              : (validation?.errors?.[0] || "—")}
          </p>
        </div>
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-3 gap-3">
        <Card
          testid="review-filled-count"
          label={r.filled}
          count={validation?.filled_fields?.length || 0}
          tone="ok"
        />
        <Card
          testid="review-missing-count"
          label={r.missing}
          count={validation?.missing_fields?.length || 0}
          tone={validation?.missing_fields?.length ? "bad" : "muted"}
        />
        <Card
          testid="review-warnings-count"
          label={r.warnings}
          count={validation?.warnings?.length || 0}
          tone={validation?.warnings?.length ? "warn" : "muted"}
        />
      </div>

      {/* Missing fields list */}
      {validation?.missing_fields?.length > 0 && (
        <div className="hairline p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {r.missing}
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {validation.missing_fields.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                <span className="font-mono text-xs">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings list */}
      {validation?.warnings?.length > 0 && (
        <div className="hairline p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {r.warnings}
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {validation.warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-[hsl(var(--warning))]" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Data summary */}
      {summary && (
        <div className="hairline">
          <div className="border-b border-border bg-secondary/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            Data Summary
          </div>
          <dl className="divide-y divide-border">
            {summary.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-3 gap-3 px-4 py-2.5 text-sm"
              >
                <dt className="col-span-1 text-muted-foreground">{row.label}</dt>
                <dd className="col-span-2 break-words">{row.value || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function Card({ testid, label, count, tone }) {
  const toneClass =
    tone === "ok"
      ? "text-[hsl(var(--primary))]"
      : tone === "bad"
      ? "text-destructive"
      : tone === "warn"
      ? "text-[hsl(var(--warning))]"
      : "text-muted-foreground";
  return (
    <div className="hairline p-4" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-2 text-3xl font-black tracking-tight ${toneClass}`}>
        {count}
      </div>
    </div>
  );
}
